import { supabase } from '../lib/supabase';

/**
 * Service functions for User Analytics
 * Handles session tracking and analytics data retrieval
 */

// =================================================================
// SESSION TRACKING
// =================================================================

/**
 * Create a new user session when user signs in
 * @param {string} userId - User ID
 * @param {Object} sessionData - Optional session metadata (ip, userAgent)
 * @returns {Promise<Object>} Created session
 */
export const createUserSession = async (userId, sessionData = {}) => {
  try {
    const { data, error } = await supabase?.from('user_sessions')?.insert([{
        user_id: userId,
        sign_in_at: new Date()?.toISOString(),
        ip_address: sessionData?.ipAddress || null,
        user_agent: sessionData?.userAgent || navigator.userAgent
      }])?.select()?.single();

    if (error) throw error;

    // Update last_sign_in_at in user_profiles
    await supabase?.from('user_profiles')?.update({ last_sign_in_at: new Date()?.toISOString() })?.eq('id', userId);

    return {
      id: data?.id,
      userId: data?.user_id,
      signInAt: data?.sign_in_at,
      ipAddress: data?.ip_address,
      userAgent: data?.user_agent
    };
  } catch (error) {
    console.error('Error creating user session:', error);
    throw error;
  }
};

/**
 * End a user session when user signs out
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} Success status
 */
export const endUserSession = async (sessionId) => {
  try {
    const { error } = await supabase?.from('user_sessions')?.update({ 
        sign_out_at: new Date()?.toISOString()
      })?.eq('id', sessionId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error ending user session:', error);
    throw error;
  }
};

/**
 * Get the current active session for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Active session or null
 */
export const getActiveSession = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_sessions')?.select('*')?.eq('user_id', userId)?.is('sign_out_at', null)?.order('sign_in_at', { ascending: false })?.limit(1)?.single();

    if (error && error?.code !== 'PGRST116') throw error;
    if (!data) return null;

    return {
      id: data?.id,
      userId: data?.user_id,
      signInAt: data?.sign_in_at,
      ipAddress: data?.ip_address,
      userAgent: data?.user_agent
    };
  } catch (error) {
    console.error('Error fetching active session:', error);
    return null;
  }
};

// =================================================================
// ANALYTICS DATA RETRIEVAL
// =================================================================

/**
 * Fetch user analytics summary for all users (admin only)
 * @param {Object} filters - Optional filters (role, dateRange)
 * @returns {Promise<Array>} Array of user analytics
 */
export const fetchUserAnalytics = async (filters = {}) => {
  try {
    let query = supabase?.from('user_analytics_summary')?.select('*')?.order('total_sessions', { ascending: false });

    if (filters?.role) {
      query = query?.eq('role', filters?.role);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data?.map(item => ({
      userId: item?.user_id,
      email: item?.email,
      fullName: item?.full_name,
      role: item?.role,
      institution: item?.institution,
      specialty: item?.specialty,
      accountCreatedAt: item?.account_created_at,
      lastSignInAt: item?.last_sign_in_at,
      totalSessions: item?.total_sessions || 0,
      totalUsageMinutes: Math.round(item?.total_usage_minutes || 0),
      avgSessionDuration: Math.round(item?.avg_session_duration || 0),
      totalRecommendations: item?.total_recommendations || 0,
      lastSessionDate: item?.last_session_date
    }));
  } catch (error) {
    console.error('Error fetching user analytics:', error);
    throw error;
  }
};

/**
 * Fetch detailed session history for a specific user
 * @param {string} userId - User ID
 * @param {number} limit - Number of sessions to fetch
 * @returns {Promise<Array>} Array of sessions
 */
export const fetchUserSessions = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase?.from('user_sessions')?.select('*')?.eq('user_id', userId)?.order('sign_in_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(session => ({
      id: session?.id,
      userId: session?.user_id,
      signInAt: session?.sign_in_at,
      signOutAt: session?.sign_out_at,
      sessionDurationMinutes: session?.session_duration_minutes,
      ipAddress: session?.ip_address,
      userAgent: session?.user_agent,
      createdAt: session?.created_at
    }));
  } catch (error) {
    console.error('Error fetching user sessions:', error);
    throw error;
  }
};

/**
 * Fetch analytics statistics for dashboard cards
 * @returns {Promise<Object>} Analytics statistics
 */
export const fetchAnalyticsStats = async () => {
  try {
    // Get total active users with sessions
    const { data: analyticsData, error: analyticsError } = await supabase?.from('user_analytics_summary')?.select('total_sessions, total_usage_minutes, total_recommendations');

    if (analyticsError) throw analyticsError;

    const totalActiveUsers = analyticsData?.filter(u => u?.total_sessions > 0)?.length;
    const totalSessions = analyticsData?.reduce((sum, u) => sum + (u?.total_sessions || 0), 0);
    const totalUsageMinutes = analyticsData?.reduce((sum, u) => sum + (u?.total_usage_minutes || 0), 0);
    let totalRecommendations = analyticsData?.reduce((sum, u) => sum + (u?.total_recommendations || 0), 0);

    // Get sessions in last 24 hours
    const yesterday = new Date();
    yesterday?.setDate(yesterday?.getDate() - 1);

    const { count: recentSessions, error: sessionsError } = await supabase?.from('user_sessions')?.select('*', { count: 'exact', head: true })?.gte('sign_in_at', yesterday?.toISOString());

    if (sessionsError) throw sessionsError;

    return {
      totalActiveUsers,
      totalSessions,
      totalUsageHours: Math.round(totalUsageMinutes / 60),
      avgUsagePerUser: totalActiveUsers > 0 ? Math.round(totalUsageMinutes / totalActiveUsers) : 0,
      totalRecommendations,
      recentSessions: recentSessions || 0
    };
  } catch (error) {
    console.error('Error fetching analytics stats:', error);
    throw error;
  }
};

/**
 * Fetch usage trends over time (for charts)
 * @param {number} days - Number of days to fetch
 * @returns {Promise<Array>} Array of daily usage data
 */
export const fetchUsageTrends = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase?.from('user_sessions')?.select('sign_in_at, session_duration_minutes')?.gte('sign_in_at', startDate?.toISOString())?.order('sign_in_at', { ascending: true });

    if (error) throw error;

    // Group by date
    const dailyData = {};
    data?.forEach(session => {
      const date = new Date(session.sign_in_at)?.toISOString()?.split('T')?.[0];
      if (!dailyData?.[date]) {
        dailyData[date] = { date, sessions: 0, totalMinutes: 0 };
      }
      dailyData[date].sessions += 1;
      dailyData[date].totalMinutes += session?.session_duration_minutes || 0;
    });

    return Object.values(dailyData)?.map(day => ({
      date: day?.date,
      sessions: day?.sessions,
      totalHours: Math.round(day?.totalMinutes / 60)
    }));
  } catch (error) {
    console.error('Error fetching usage trends:', error);
    throw error;
  }
};

/**
 * Fetch imaging type usage statistics from clinical recommendations
 * @param {number} days - Number of days to analyze (default 30)
 * @returns {Promise<Object>} Imaging type statistics with counts and percentages
 */
export const fetchImagingTypeUsage = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase
      ?.from('clinical_recommendations')
      ?.select('recommended_modality, body_region, created_at')
      ?.gte('created_at', startDate?.toISOString());

    if (error) throw error;

    // Count by imaging type
    const imagingTypes = {};
    let totalRecommendations = 0;

    data?.forEach(rec => {
      const modality = rec?.recommended_modality || 'Unknown';
      if (!imagingTypes?.[modality]) {
        imagingTypes[modality] = { count: 0, bodyRegions: {} };
      }
      imagingTypes[modality].count += 1;
      totalRecommendations += 1;

      // Track body regions for this modality
      const region = rec?.body_region || 'Unknown';
      if (!imagingTypes?.[modality]?.bodyRegions?.[region]) {
        imagingTypes[modality].bodyRegions[region] = 0;
      }
      imagingTypes[modality].bodyRegions[region] += 1;
    });

    // Convert to array with percentages
    const imagingStats = Object.entries(imagingTypes)?.map(([modality, data]) => ({
      modality,
      count: data?.count,
      percentage: totalRecommendations > 0 ? ((data?.count / totalRecommendations) * 100)?.toFixed(1) : 0,
      topBodyRegions: Object.entries(data?.bodyRegions)
        ?.sort((a, b) => b?.[1] - a?.[1])
        ?.slice(0, 3)
        ?.map(([region, count]) => ({ region, count }))
    }))?.sort((a, b) => b?.count - a?.count);

    return {
      success: true,
      totalRecommendations,
      imagingTypes: imagingStats,
      periodDays: days
    };
  } catch (error) {
    console.error('Error fetching imaging type usage:', error);
    throw error;
  }
};

/**
 * Fetch referral patterns by region from user profiles and recommendations
 * @param {number} days - Number of days to analyze (default 30)
 * @returns {Promise<Object>} Regional referral statistics
 */
export const fetchReferralPatternsByRegion = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase
      ?.from('clinical_recommendations')
      ?.select(`
        id,
        created_at,
        recommended_modality,
        user_id,
        user_profiles (
          institution_country,
          institution_name,
          specialization
        )
      `)
      ?.gte('created_at', startDate?.toISOString());

    if (error) throw error;

    // Group by region/country
    const regionalData = {};
    let totalReferrals = 0;

    data?.forEach(rec => {
      const country = rec?.user_profiles?.institution_country || 'Unknown';
      const institution = rec?.user_profiles?.institution_name || 'Unknown';
      const specialty = rec?.user_profiles?.specialization || 'Unknown';
      const modality = rec?.recommended_modality || 'Unknown';

      if (!regionalData?.[country]) {
        regionalData[country] = {
          referrals: 0,
          institutions: new Set(),
          specialties: {},
          modalities: {}
        };
      }

      regionalData[country].referrals += 1;
      regionalData?.[country]?.institutions?.add(institution);
      totalReferrals += 1;

      // Track specialties
      if (!regionalData?.[country]?.specialties?.[specialty]) {
        regionalData[country].specialties[specialty] = 0;
      }
      regionalData[country].specialties[specialty] += 1;

      // Track modalities
      if (!regionalData?.[country]?.modalities?.[modality]) {
        regionalData[country].modalities[modality] = 0;
      }
      regionalData[country].modalities[modality] += 1;
    });

    // Convert to array with formatted data
    const regionalStats = Object.entries(regionalData)?.map(([country, data]) => ({
      country,
      referrals: data?.referrals,
      percentage: totalReferrals > 0 ? ((data?.referrals / totalReferrals) * 100)?.toFixed(1) : 0,
      institutionCount: data?.institutions?.size,
      topSpecialties: Object.entries(data?.specialties)
        ?.sort((a, b) => b?.[1] - a?.[1])
        ?.slice(0, 3)
        ?.map(([specialty, count]) => ({ specialty, count })),
      topModalities: Object.entries(data?.modalities)
        ?.sort((a, b) => b?.[1] - a?.[1])
        ?.slice(0, 3)
        ?.map(([modality, count]) => ({ modality, count }))
    }))?.sort((a, b) => b?.referrals - a?.referrals);

    return {
      success: true,
      totalReferrals,
      regionalPatterns: regionalStats,
      periodDays: days
    };
  } catch (error) {
    console.error('Error fetching referral patterns by region:', error);
    throw error;
  }
};

/**
 * Fetch clinical outcome metrics from recommendations with feedback
 * @param {number} days - Number of days to analyze (default 30)
 * @returns {Promise<Object>} Clinical outcome statistics
 */
export const fetchClinicalOutcomeMetrics = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase
      ?.from('clinical_recommendations')
      ?.select('id, confidence_score, feedback_rating, feedback_notes, recommended_modality, body_region, created_at')
      ?.gte('created_at', startDate?.toISOString());

    if (error) throw error;

    // Calculate outcome metrics
    const metrics = {
      totalRecommendations: data?.length || 0,
      withFeedback: 0,
      avgConfidenceScore: 0,
      avgFeedbackRating: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      highConfidenceRate: 0,
      modalityAccuracy: {}
    };

    let totalConfidence = 0;
    let totalFeedbackRating = 0;
    let highConfidenceCount = 0;

    data?.forEach(rec => {
      // Confidence scores
      const confidence = parseFloat(rec?.confidence_score) || 0;
      totalConfidence += confidence;
      if (confidence >= 0.9) highConfidenceCount += 1;

      // Feedback ratings
      if (rec?.feedback_rating) {
        metrics.withFeedback += 1;
        totalFeedbackRating += rec?.feedback_rating;
        metrics.ratingDistribution[rec?.feedback_rating] += 1;

        // Track modality accuracy based on feedback
        const modality = rec?.recommended_modality || 'Unknown';
        if (!metrics?.modalityAccuracy?.[modality]) {
          metrics.modalityAccuracy[modality] = {
            count: 0,
            avgRating: 0,
            totalRating: 0
          };
        }
        metrics.modalityAccuracy[modality].count += 1;
        metrics.modalityAccuracy[modality].totalRating += rec?.feedback_rating;
      }
    });

    // Calculate averages
    if (metrics?.totalRecommendations > 0) {
      metrics.avgConfidenceScore = (totalConfidence / metrics?.totalRecommendations)?.toFixed(3);
      metrics.highConfidenceRate = ((highConfidenceCount / metrics?.totalRecommendations) * 100)?.toFixed(1);
    }

    if (metrics?.withFeedback > 0) {
      metrics.avgFeedbackRating = (totalFeedbackRating / metrics?.withFeedback)?.toFixed(2);
    }

    // Calculate modality accuracy averages
    Object.keys(metrics?.modalityAccuracy)?.forEach(modality => {
      const data = metrics?.modalityAccuracy?.[modality];
      data.avgRating = (data?.totalRating / data?.count)?.toFixed(2);
      delete data?.totalRating; // Remove intermediate value
    });

    // Convert to sorted array
    const modalityAccuracyArray = Object.entries(metrics?.modalityAccuracy)
      ?.map(([modality, data]) => ({
        modality,
        recommendationCount: data?.count,
        avgRating: parseFloat(data?.avgRating)
      }))
      ?.sort((a, b) => b?.avgRating - a?.avgRating);

    return {
      success: true,
      metrics: {
        ...metrics,
        modalityAccuracy: modalityAccuracyArray,
        feedbackRate: ((metrics?.withFeedback / metrics?.totalRecommendations) * 100)?.toFixed(1)
      },
      periodDays: days
    };
  } catch (error) {
    console.error('Error fetching clinical outcome metrics:', error);
    throw error;
  }
};

// =================================================================
// REVENUE ANALYTICS (STRIPE PAYMENTS)
// =================================================================

/**
 * Fetch revenue analytics from Stripe payments
 * @param {number} days - Number of days to analyze
 * @returns {Promise<Object>} Revenue analytics data
 */
export const fetchRevenueAnalytics = async (days = 30) => {
  try {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    const { data, error } = await supabase
      ?.from('credit_purchases')
      ?.select(`
        *,
        user_profiles (
          full_name,
          email,
          institution_name
        )
      `)
      ?.gte('created_at', startDate?.toISOString())
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    // Calculate revenue statistics
    const stats = {
      totalRevenue: 0,
      completedRevenue: 0,
      pendingRevenue: 0,
      totalTransactions: data?.length || 0,
      completedTransactions: 0,
      totalCreditsSold: 0,
      avgTransactionValue: 0,
      revenueByPackage: {},
      dailyRevenue: {}
    };

    data?.forEach(purchase => {
      const amount = (purchase?.amount_cents || 0) / 100; // Convert cents to dollars
      stats.totalRevenue += amount;

      if (purchase?.status === 'completed') {
        stats.completedRevenue += amount;
        stats.completedTransactions += 1;
        stats.totalCreditsSold += purchase?.credits_purchased || 0;
      } else if (purchase?.status === 'pending') {
        stats.pendingRevenue += amount;
      }

      // Track by package type (extract from metadata or credits)
      const credits = purchase?.credits_purchased || 0;
      let packageType = 'Custom';
      if (credits === 25) packageType = 'Starter';
      else if (credits === 100) packageType = 'Value';
      else if (credits === 250) packageType = 'Professional';
      else if (credits === 500) packageType = 'Enterprise';

      if (!stats?.revenueByPackage?.[packageType]) {
        stats.revenueByPackage[packageType] = {
          revenue: 0,
          count: 0,
          credits: 0
        };
      }
      stats.revenueByPackage[packageType].revenue += amount;
      stats.revenueByPackage[packageType].count += 1;
      stats.revenueByPackage[packageType].credits += credits;

      // Daily revenue tracking
      const date = new Date(purchase?.created_at)?.toISOString()?.split('T')?.[0];
      if (!stats?.dailyRevenue?.[date]) {
        stats.dailyRevenue[date] = {
          date,
          revenue: 0,
          transactions: 0,
          credits: 0
        };
      }
      if (purchase?.status === 'completed') {
        stats.dailyRevenue[date].revenue += amount;
        stats.dailyRevenue[date].transactions += 1;
        stats.dailyRevenue[date].credits += credits;
      }
    });

    stats.avgTransactionValue = stats?.completedTransactions > 0 
      ? stats?.completedRevenue / stats?.completedTransactions 
      : 0;

    return {
      success: true,
      stats,
      transactions: data?.map(purchase => ({
        id: purchase?.id,
        userId: purchase?.user_id,
        userName: purchase?.user_profiles?.full_name,
        userEmail: purchase?.user_profiles?.email,
        institution: purchase?.user_profiles?.institution_name,
        amount: (purchase?.amount_cents || 0) / 100,
        credits: purchase?.credits_purchased,
        status: purchase?.status,
        paymentMethod: purchase?.payment_method,
        stripePaymentIntentId: purchase?.stripe_payment_intent_id,
        createdAt: purchase?.created_at,
        completedAt: purchase?.completed_at
      })) || [],
      dailyRevenue: Object.values(stats?.dailyRevenue)?.sort((a, b) => 
        new Date(a?.date) - new Date(b?.date)
      )
    };
  } catch (error) {
    console.error('Error fetching revenue analytics:', error);
    throw error;
  }
};

/**
 * Fetch top revenue generating users
 * @param {number} limit - Number of top users to fetch
 * @returns {Promise<Array>} Top revenue users
 */
export const fetchTopRevenueUsers = async (limit = 10) => {
  try {
    const { data, error } = await supabase
      ?.from('credit_purchases')
      ?.select(`
        user_id,
        amount_cents,
        credits_purchased,
        user_profiles (
          full_name,
          email,
          institution_name,
          specialization
        )
      `)
      ?.eq('status', 'completed');

    if (error) throw error;

    // Group by user and calculate totals
    const userRevenue = {};
    data?.forEach(purchase => {
      const userId = purchase?.user_id;
      if (!userRevenue?.[userId]) {
        userRevenue[userId] = {
          userId,
          fullName: purchase?.user_profiles?.full_name,
          email: purchase?.user_profiles?.email,
          institution: purchase?.user_profiles?.institution_name,
          specialty: purchase?.user_profiles?.specialization,
          totalRevenue: 0,
          totalCredits: 0,
          purchaseCount: 0
        };
      }
      userRevenue[userId].totalRevenue += (purchase?.amount_cents || 0) / 100;
      userRevenue[userId].totalCredits += purchase?.credits_purchased || 0;
      userRevenue[userId].purchaseCount += 1;
    });

    // Sort by revenue and return top users
    return Object.values(userRevenue)
      ?.sort((a, b) => b?.totalRevenue - a?.totalRevenue)
      ?.slice(0, limit);
  } catch (error) {
    console.error('Error fetching top revenue users:', error);
    throw error;
  }
};

/**
 * Fetch revenue comparison (current vs previous period)
 * @param {number} days - Period in days to compare
 * @returns {Promise<Object>} Revenue comparison data
 */
export const fetchRevenueComparison = async (days = 30) => {
  try {
    const currentEndDate = new Date();
    const currentStartDate = new Date();
    currentStartDate?.setDate(currentStartDate?.getDate() - days);

    const previousStartDate = new Date(currentStartDate);
    previousStartDate?.setDate(previousStartDate?.getDate() - days);

    // Fetch current period
    const { data: currentData, error: currentError } = await supabase
      ?.from('credit_purchases')
      ?.select('amount_cents, credits_purchased, status')
      ?.gte('created_at', currentStartDate?.toISOString())
      ?.lte('created_at', currentEndDate?.toISOString())
      ?.eq('status', 'completed');

    if (currentError) throw currentError;

    // Fetch previous period
    const { data: previousData, error: previousError } = await supabase
      ?.from('credit_purchases')
      ?.select('amount_cents, credits_purchased, status')
      ?.gte('created_at', previousStartDate?.toISOString())
      ?.lt('created_at', currentStartDate?.toISOString())
      ?.eq('status', 'completed');

    if (previousError) throw previousError;

    const calculateStats = (data) => ({
      revenue: data?.reduce((sum, p) => sum + ((p?.amount_cents || 0) / 100), 0),
      transactions: data?.length,
      credits: data?.reduce((sum, p) => sum + (p?.credits_purchased || 0), 0)
    });

    const current = calculateStats(currentData || []);
    const previous = calculateStats(previousData || []);

    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      current,
      previous,
      changes: {
        revenue: calculateChange(current?.revenue, previous?.revenue),
        transactions: calculateChange(current?.transactions, previous?.transactions),
        credits: calculateChange(current?.credits, previous?.credits)
      }
    };
  } catch (error) {
    console.error('Error fetching revenue comparison:', error);
    throw error;
  }
};